import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Swords, Trophy, Wallet, AlertCircle, UserPlus, Users, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { formatRelativeTime, cn } from '@/lib/utils';

const notificationIcons: Record<string, React.ElementType> = {
  wager_accepted: Swords,
  challenge_received: Swords,
  match_result: Trophy,
  winnings_ready: Wallet,
  funding_reminder: AlertCircle,
  friend_request: UserPlus,
  friend_accepted: Users,
  message_received: MessageSquare,
  default: Bell,
};

function getNotificationUrl(notification: { type: string; data?: Record<string, unknown> }): string | null {
  const data = notification.data;
  if (data?.actionUrl && typeof data.actionUrl === 'string') {
    return data.actionUrl;
  }
  const wagerId = data?.wagerId as string | undefined;
  if (wagerId) {
    return `/wagers/${wagerId}`;
  }
  return null;
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.getNotifications(),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unreadCount = notifications?.filter((n) => !n.readAt).length || 0;

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Notifications</h1>
          <p className="text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications */}
      {isLoading ? (
        <div className="flex h-60 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : notifications && notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notification, i) => {
            const Icon = notificationIcons[notification.type] || notificationIcons.default;
            const isUnread = !notification.readAt;

            return (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card
                  className={cn(
                    'cursor-pointer transition-colors hover:border-primary/50',
                    isUnread && 'border-primary/30 bg-primary/5'
                  )}
                  onClick={() => {
                    if (isUnread) {
                      markReadMutation.mutate(notification.id);
                    }
                    const url = getNotificationUrl(notification);
                    if (url) {
                      navigate(url);
                    }
                  }}
                >
                  <CardContent className="flex items-start gap-4 p-4">
                    <div
                      className={cn(
                        'rounded-xl p-2',
                        isUnread ? 'bg-primary/10' : 'bg-muted'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5',
                          isUnread ? 'text-primary' : 'text-muted-foreground'
                        )}
                      />
                    </div>
                    <div className="flex-1">
                      <p className={cn('font-medium', isUnread && 'text-foreground')}>
                        {notification.title}
                      </p>
                      {notification.body && (
                        <p className="mt-1 text-sm text-muted-foreground">{notification.body}</p>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        {formatRelativeTime(notification.createdAt)}
                      </p>
                    </div>
                    {isUnread && <div className="h-2 w-2 rounded-full bg-primary" />}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex h-60 flex-col items-center justify-center text-center">
            <Bell className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="font-semibold">No notifications</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              You're all caught up! Check back later for updates.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
